import React from "react";
import {
  Navigation,
  Page,
  Storage,
  TaskMultiSelect,
  TaskExternal,
} from "@bittnerdevelopmentgroup/interactive";
import Simulation from "./Simulation";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Button } from "@wwnds/react";

import panel4a from "./Int_PrinEco4_8.4-1_fig8.1.png";
import panel4b from "./Int_PrinEco4_8.4-2_fig8.1.png";

function GraphSim({ simstate, timescale }) {
  let [numWorkers, setNumWorkers] = React.useState(1);
  React.useEffect(() => {
    let interval = window.setInterval(() => {
      if (numWorkers >= 12) {
        setNumWorkers(1)
      } else {
        setNumWorkers(numWorkers + 1)
      }
    }, 2000)
    return () => {
      window.clearInterval(interval)
    }
  })
  let clip1 = 60 / 285 * 100
  let clip10 = 222 / 285 * 100
  let clip = ((numWorkers - 1) / 9) * (clip10 - clip1) + clip1
  return <Row>
    <Col xs={6} className="pr-0">
      <Simulation
        state={simstate}
        timescale={timescale}
        numWorkers={Math.min(10, numWorkers)}
        ariaDescribedBy="A fast food kitchen in which the number of workers gradually increases from 1 to 10."
      />
    </Col>
    <Col xs={6} className="pl-0">
      <div style={{ position: 'relative' }}>
        <img
          src={panel4a}
          alt=""
          width="100%"
          aria-hidden="true"
        />
        <img
          src={panel4b}
          style={{
            position: 'absolute',
            left: 0,
            clipPath: `inset(0 ${100 - clip}% 0 0)`,
            transition: 'clip-path 0.5s',
          }}
          alt="A graph of Total hourly Output as a function of Number of Workers. The curve starts at the origin, climbs to a peak at 8 workers, 67 meals, then declines as the number of workers increases further."
          width="100%"
        />

      </div>

    </Col>
  </Row>
}

function useSavedState(state, field, def) {
  let [x, setX] = React.useState(state[field] ?? def)
  return [x, (val) => {
    state[field] = val
    setX(val)
  }]
}

function AddWorkers({ state, taskState, numWorkers1, numWorkers2, numMeals1, numMeals2, alt1, alt2, ariaDescribedBy }) {
  let [numWorkers, setNumWorkers] = useSavedState(taskState, "numWorkers", numWorkers1)
  let [numMeals, setNumMeals] = useSavedState(taskState, "numMeals", numMeals1)
  let [buttonEnabled, setButtonEnabled] = useSavedState(taskState, "buttonEnabled", true)
  let [taskComplete, setTaskComplete] = useSavedState(taskState, "taskComplete", false)
  let [alt, setAlt] = useSavedState(taskState, "alt", alt1)
  return <figure style={{ width: "80%" }}>
    <figcaption>
      <b># of workers: <span style={{ color: buttonEnabled ? "#000000" : "#0584ad" }}>{numWorkers}</span></b><br />
      <b># of meals per hour: <span style={{ color: buttonEnabled ? "#000000" : "#e48038" }}>{numMeals}</span></b>
      <span className="float-right">
        <Button style={{ marginBottom: "16px" }} color="primary" variant="solid" disabled={!buttonEnabled} onClick={() => {
          setNumWorkers(numWorkers2)
          setNumMeals(numMeals2)
          setAlt(alt2)
          setButtonEnabled(false)
          window.setTimeout(() => {
            setTaskComplete(true)
          }, 5000)
        }}>
          Add {numWorkers2 - numWorkers1} workers
        </Button>
      </span>
    </figcaption>
    <Simulation
      state={state}
      timescale={1}
      numWorkers={numWorkers}
      ariaDescribedBy={ariaDescribedBy}
    />
    <div
      id={ariaDescribedBy}
      className="sr-only"
    >
      {alt}
    </div>
    <TaskExternal state={taskState} completed={taskComplete} />
  </figure>
}

class ProductionFunction extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      numWorkersP1: '1'
    }
  }

  render() {
    let SimState = Storage()
    return (
      <div className="productionFunction">
        <div className="sr-only" aria-hidden="true">
          {[panel4a, panel4b].map(src => <img alt="" src={src} />)}
        </div>
        <Navigation interactiveId="chapter-8b">
          <Page>
            <p>
              The following animation shows a fast-food kitchen where one worker does absolutely everything.
              Running from the sink to the storage closet to the food prep station to the grill makes him pretty slow
              to produce burgers. With only one worker making burgers, the kitchen produces a total of five meals
              per hour.
            </p>
            <p>
              <b>What would happen if he had help?</b>
            </p>
            <p>
              <i>Select the "Add 2 workers" button to observe what happens when more workers are added to the kitchen.</i>
            </p>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <AddWorkers
                state={SimState}
                taskState={Storage()}
                numWorkers1={1}
                numWorkers2={3}
                numMeals1={5}
                numMeals2={30}
                ariaDescribedBy="p1"
                alt1={<>A fast food kitchen, with a large, L shaped cooking station, a storage area, sinks for washing, and a food prep counter. 1 worker who moves freely about the kitchen can prepare 5 meals per hour.</>}
                alt2={<>3 workers who move about the kitchen can prepare 30 meals per hour. However, they occasionally run into one another.</>}
              />
            </div>
          </Page>
          <Page>
            <p>
              At first, adding workers greatly increases output. Not only are there more people on the job, but they
              can specialize to be more efficient. One worker washes dishes, one worker runs the grill, and so on.
              However, eventually, the benefit of adding more people starts to decline. They're still helpful, but not
              quite as much.
            </p>
            <p>
              <i>Select the "Add 5 workers" button to observe what happens when more workers are added to the kitchen.</i>
            </p>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <AddWorkers
                state={SimState}
                taskState={Storage()}
                numWorkers1={3}
                numWorkers2={8}
                numMeals1={30}
                numMeals2={67}
                ariaDescribedBy="p2"
                alt1={<>3 workers who move about the kitchen can prepare 30 meals per hour. However, they occasionally run into one another.</>}
                alt2={<>8 workers who move about the kitchen can prepare 67 meals per hour. They run into one another constantly.</>}
              />
            </div>
          </Page>
          <Page>
            <p>
              As the kitchen gets more crowded, workers start bumping into each other and have to take turns using the equipment. Eventually, things get so bad that the burger output starts to go down, not up.
            </p>
            <p>
              <i>Select the "Add 2 workers" button to observe what happens when more workers are added to the kitchen.</i>
            </p>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <AddWorkers
                state={SimState}
                taskState={Storage()}
                numWorkers1={8}
                numWorkers2={10}
                numMeals1={67}
                numMeals2={56}
                ariaDescribedBy="p3"
                alt1={<>8 workers who move about the kitchen can prepare 67 meals per hour. They run into one another constantly.</>}
                alt2={<>10 workers who move about the kitchen can prepare only 56 meals per hour. They run into one another so often that they get less done than 2 fewer workers would.</>}
              />
            </div>
          </Page>
          <Page>
            <p>
              So what does that look like graphically? The change in meals produced when each worker is added is 
              called the <b>marginal product of labor</b>. The following animations show that eventually each additional 
              worker starts contributing fewer and fewer meals—economists would describe this as <b>diminishing 
              marginal product of labor</b>. Toward the end, when the additional workers are getting in the way and 
              slowing their colleagues down, the result is a <b>negative marginal product of labor</b>.
            </p>
            <GraphSim
              simstate={SimState}
              timescale={3}
            />
          </Page>
          <Page>
            <p>
              You can't keep adding workers to a kitchen forever and expect them to be productive, because there are only so many resources in a kitchen.
            </p>
            <TaskMultiSelect
              key="pfpanel5"
              state={Storage()}
              prompt={<><b>Which factors limit how much these workers can produce? </b><i>Select all that apply.</i></>}
              data={[
                {
                  label: (
                    <React.Fragment>
                      <>
                        the square footage of the kitchen
                      </>
                    </React.Fragment>
                  ),
                  correct: true,
                },
                {
                  label: (
                    <React.Fragment>
                      <>
                        the number of spatulas
                      </>
                    </React.Fragment>
                  ),
                  correct: true,
                },
                {
                  label: (
                    <React.Fragment>
                      <>
                        room at the sink
                      </>
                    </React.Fragment>
                  ),
                  correct: true,
                },
                {
                  label: (
                    <React.Fragment>
                      <>
                        the number of grills
                      </>
                    </React.Fragment>
                  ),
                  correct: true,
                },
              ]}
              correctFeedback={
                <p>
                  These are all limiting factors in the kitchen's productivity. Because of these limitations, marginal product of labor in this case will start to diminish and then turn negative once a certain number of workers is added.
                </p>
              }
              incorrectFeedback="Review the other options listed. Are any of these unlimited in a kitchen?"
            />
          </Page>
        </Navigation>
      </div>
    )
  }
}

export default ProductionFunction
